import { useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Button,
  LinearProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAttachmentStore } from '../../stores/root_store';
import type { CombinedAttachment } from '../../api/attachment_api';

interface AttachmentsCardProps {
  requestKey: string;
  requestUuid: string;
  canWork?: boolean;
}

const getFileIcon = (contentType: string | null | undefined) => {
  if (!contentType) {
    return <InsertDriveFileIcon color="action" />;
  }
  if (contentType.startsWith('image/')) {
    return <ImageIcon color="primary" />;
  }
  if (contentType === 'application/pdf') {
    return <PictureAsPdfIcon color="error" />;
  }
  return <InsertDriveFileIcon color="action" />;
};

const formatFileSize = (bytes: number | null): string => {
  if (bytes === null || bytes === 0) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const AttachmentsCard = observer(function AttachmentsCard({
  requestKey,
  requestUuid,
  canWork = false,
}: AttachmentsCardProps) {
  const attachmentStore = useAttachmentStore();
  const [dragOver, setDragOver] = useState(false);

  const attachments = attachmentStore.getAttachmentsByRequest(requestKey);
  const loading = attachmentStore.loading;
  const uploading = attachmentStore.uploading;

  useEffect(() => {
    if (requestKey) {
      attachmentStore.fetchByRequest(requestKey);
    }
  }, [requestKey, attachmentStore]);

  const handleDownload = async (attachment: CombinedAttachment) => {
    await attachmentStore.downloadAttachment(attachment.key, 'attachment');
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      for (const file of files) {
        await attachmentStore.uploadAttachment(requestKey, requestUuid, file);
      }
    },
    [attachmentStore, requestKey, requestUuid]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      for (const file of files) {
        await attachmentStore.uploadAttachment(requestKey, requestUuid, file);
      }

      // Reset input
      e.target.value = '';
    },
    [attachmentStore, requestKey, requestUuid]
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AttachFileIcon color="primary" />
          <Typography variant="h6">Attachments</Typography>
          {loading && <CircularProgress size={16} />}
        </Box>

        {/* Drop zone - only show if user can work on the request */}
        {canWork && (
          <>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                p: 2,
                mb: 2,
                textAlign: 'center',
                bgcolor: dragOver ? 'primary.50' : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              {uploading ? (
                <Box>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Uploading...
                  </Typography>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Drag and drop files here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    or
                  </Typography>
                  <Button
                    component="label"
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Browse Files
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={handleFileSelect}
                    />
                  </Button>
                </>
              )}
            </Box>

            {/* Upload progress */}
            {uploading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
              </Box>
            )}
          </>
        )}

        {/* Attachments list */}
        {attachments.length > 0 ? (
          <List dense disablePadding>
            {attachments.map((attachment) => (
              <ListItem key={attachment.key} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getFileIcon(attachment.content_type)}
                </ListItemIcon>
                <ListItemText
                  primary={attachment.filename}
                  secondary={formatFileSize(attachment.filesize)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                    sx: { maxWidth: 160 },
                  }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDownload(attachment)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : !loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            No attachments yet
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
});
